import { Directive, ElementRef, forwardRef, HostListener, inject, Input } from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'

const MAX_DIGITS = 12

@Directive({
  selector: 'input[currencyMask]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyMaskDirective),
      multi: true,
    },
  ],
})
export class CurrencyMaskDirective implements ControlValueAccessor {
  private readonly el = inject(ElementRef<HTMLInputElement>)

  @Input() public currencyPrefix = 'R$ '

  private centValue: number | null = null
  private onChange: (value: number | null) => void = () => {}
  private onTouched: () => void = () => {}

  public writeValue(value: number | null | undefined): void {
    if (value === null || value === undefined) {
      this.centValue = null
    } else {
      this.centValue = Math.round(value * 100)
    }

    this.updateDisplay()
  }

  public registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  public setDisabledState(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled
  }

  @HostListener('keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey) return

    if (this.isNavigationKey(event.key)) return

    if (event.key >= '0' && event.key <= '9') {
      event.preventDefault()
      const digit = Number(event.key)
      const newValue = (this.centValue ?? 0) * 10 + digit

      if (String(newValue).length > MAX_DIGITS) return

      this.centValue = newValue
      this.updateDisplay()
      this.emitChange()

      return
    }

    if (event.key === 'Backspace') {
      event.preventDefault()
      const current = this.centValue ?? 0
      const next = Math.floor(current / 10)
      this.centValue = next === 0 ? null : next
      this.updateDisplay()
      this.emitChange()

      return
    }

    if (event.key === 'Delete') {
      event.preventDefault()
      this.centValue = null
      this.updateDisplay()
      this.emitChange()

      return
    }

    event.preventDefault()
  }

  @HostListener('paste', ['$event'])
  public onPaste(event: ClipboardEvent): void {
    event.preventDefault()
    const pastedText = event.clipboardData?.getData('text') ?? ''
    const digits = pastedText.replaceAll(/\D/g, '')

    if (!digits) return

    let newValue = Number.parseInt(digits, 10)
    if (String(newValue).length > MAX_DIGITS) {
      newValue = Number.parseInt(digits.slice(0, MAX_DIGITS), 10)
    }

    this.centValue = newValue === 0 ? null : newValue
    this.updateDisplay()
    this.emitChange()
  }

  @HostListener('input')
  public onInput(): void {
    const input = this.el.nativeElement
    const digits = input.value.replaceAll(/\D/g, '')

    if (digits) {
      let parsed = Number.parseInt(digits, 10)
      if (String(parsed).length > MAX_DIGITS) {
        parsed = Number.parseInt(digits.slice(0, MAX_DIGITS), 10)
      }

      this.centValue = parsed === 0 ? null : parsed
    } else {
      this.centValue = null
    }

    this.updateDisplay()
    this.emitChange()
  }

  @HostListener('blur')
  public onBlur(): void {
    this.onTouched()
  }

  private isNavigationKey(key: string): boolean {
    return (
      ['Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Escape', 'Enter', 'Shift', 'CapsLock'].includes(
        key,
      ) || key.startsWith('F')
    )
  }

  private updateDisplay(): void {
    this.el.nativeElement.value = this.formatCents(this.centValue)
  }

  private formatCents(cents: number | null): string {
    if (cents === null || cents === 0) return ''

    const str = String(cents).padStart(3, '0')
    const decimalPart = str.slice(-2)
    const integerPart = str.slice(0, -2) || '0'

    return this.currencyPrefix + this.addThousandSeparators(integerPart) + ',' + decimalPart
  }

  private addThousandSeparators(value: string): string {
    let result = ''
    let count = 0

    for (let idx = value.length - 1; idx >= 0; idx--) {
      if (count > 0 && count % 3 === 0) {
        result = '.' + result
      }

      result = value[idx] + result
      count++
    }

    return result
  }

  private emitChange(): void {
    this.onChange(this.centValue === null ? null : this.centValue / 100)
  }
}
